import React, { useEffect, useState } from 'react';

const adjustmentTypes = ['DAMAGE', 'EXPIRY_DISPOSAL', 'WASTAGE', 'THEFT_LOSS', 'PHYSICAL_COUNT', 'MANUAL_PLUS', 'MANUAL_MINUS'];

function FoodStockAdjustment({ token, selectedProperty }) {
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ store_id: '', item_id: '', adjustment_type: 'DAMAGE', qty_change: '', reason: '', unit_cost: '', expiry_date: '' });

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId, ...extra });
  const parseResponse = async (res) => {
    const raw = await res.text();
    try { const json = raw ? JSON.parse(raw) : {}; return { data: json, message: json?.error || '' }; } catch { return { data: {}, message: raw || '' }; }
  };

  const load = async () => {
    if (!chainId || !propertyId) return;
    try {
      const [sRes, iRes, aRes] = await Promise.all([
        fetch('/api/v1/food/masters/stores', { headers: headers() }),
        fetch('/api/v1/food/masters/items', { headers: headers() }),
        fetch('/api/v1/food/adjustments', { headers: headers() })
      ]);
      const s = await parseResponse(sRes); const i = await parseResponse(iRes); const a = await parseResponse(aRes);
      if (!sRes.ok) return setError(s.message || 'Failed to load stores');
      if (!iRes.ok) return setError(i.message || 'Failed to load items');
      if (!aRes.ok) return setError(a.message || 'Failed to load adjustments');
      setStores(Array.isArray(s.data) ? s.data : []);
      setItems(Array.isArray(i.data) ? i.data : []);
      setRows(Array.isArray(a.data) ? a.data : []);
    } catch { setError('Network error while loading adjustments'); }
  };

  useEffect(() => {
    if (!chainId || !propertyId) return setError('Select a property first from Hotel Config');
    setError(''); setMessage(''); load();
  }, [token, chainId, propertyId]);

  const submit = async () => {
    setError(''); setMessage('');
    if (!form.store_id || !form.item_id || !form.adjustment_type) return setError('Store, item and adjustment type are required');
    const qty = Number(form.qty_change || 0);
    if (Number.isNaN(qty) || qty === 0) return setError('Qty change must be non-zero');
    if (!form.reason) return setError('Reason is required');
    try {
      const res = await fetch('/api/v1/food/adjustments', {
        method: 'POST', headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          store_id: form.store_id,
          item_id: form.item_id,
          adjustment_type: form.adjustment_type,
          qty_change: qty,
          reason: form.reason,
          unit_cost: Number(form.unit_cost || 0),
          expiry_date: form.expiry_date || null
        })
      });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Adjustment failed');
      setMessage('Adjustment posted');
      setForm({ store_id: '', item_id: '', adjustment_type: 'DAMAGE', qty_change: '', reason: '', unit_cost: '', expiry_date: '' });
      load();
    } catch { setError('Network error while posting adjustment'); }
  };

  const input = { padding: '0.5rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnBase = { padding: '0.5rem 0.85rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Stock Adjustment</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}
      <div className="filter-bar">
        <h3>New Adjustment</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.6rem' }}>
          <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} style={input}><option value="">Store</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}</select>
          <select value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} style={input}><option value="">Item</option>{items.map((i) => <option key={i.id} value={i.id}>{`${i.item_code} - ${i.item_name}`}</option>)}</select>
          <select value={form.adjustment_type} onChange={(e) => setForm({ ...form, adjustment_type: e.target.value })} style={input}>{adjustmentTypes.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          <input type="number" step="0.001" value={form.qty_change} onChange={(e) => setForm({ ...form, qty_change: e.target.value })} style={input} placeholder="Qty Change (+/-)" />
          <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={input} placeholder="Reason" />
          <input type="number" step="0.0001" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} style={input} placeholder="Unit Cost (for positive)" />
          <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} style={input} />
        </div>
        <div style={{ marginTop: '0.6rem' }}><button onClick={submit} className="btn-success" style={btnBase}>Post Adjustment</button></div>
      </div>
      <div className="filter-bar">
        <h3>Adjustment Log</h3>
        {rows.length === 0 ? <p>No adjustments</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead><tr style={{ background: '#2f855a', color: '#fff' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Store</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Item</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Type</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Qty</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Reason</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Approved By</th>
            </tr></thead>
            <tbody>{rows.map((r) => <tr key={r.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
              <td style={{ padding: '0.5rem' }}>{r.store_name}</td><td style={{ padding: '0.5rem' }}>{`${r.item_code || ''} ${r.item_name || ''}`}</td><td style={{ padding: '0.5rem' }}>{r.adjustment_type}</td><td style={{ padding: '0.5rem' }}>{Number(r.qty_change || 0).toFixed(3)}</td><td style={{ padding: '0.5rem' }}>{r.reason}</td><td style={{ padding: '0.5rem' }}>{r.approved_by || '-'}</td>
            </tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodStockAdjustment;
